import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import {
  getInboxAttentionSummaryAction,
  getInboxFilterOptionsAction,
  getInboxSourceCapabilitiesAction,
  getUnifiedThreadAction,
  listUnifiedInboxAction,
} from "@/server-actions/unified-inbox"
import type { UnifiedInboxFilters } from "@/lib/inbox/types"
import { boundedRead } from "./bounded-read"

export const UNIFIED_INBOX_QUERY_KEY = ["unified-inbox"] as const

const PAGE_SIZE = 25

const LIST_POLL_MS = 10_000

/**
 * Pages past this stop the poll: React Query refetches every loaded page of an infinite
 * query. With a `Load more` button the operator knew they had left the first page; a
 * sentinel accumulates them silently, so freezing after one would stop the inbox updating
 * merely because somebody scrolled. Three is 75 rows — an ordinary look — and beyond it
 * the reader is on old conversations, where window focus is refresh enough.
 */
const POLLED_PAGES = 3

/** Matches the agent inbox's own message poll. */
const THREAD_POLL_MS = 5_000

export function useUnifiedInbox(filters: UnifiedInboxFilters, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    enabled: options.enabled ?? true,
    queryKey: [...UNIFIED_INBOX_QUERY_KEY, "list", filters],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const result = await boundedRead(listUnifiedInboxAction({
        cursor: pageParam,
        limit: PAGE_SIZE,
        channel_types: filters.channelTypes,
        agent: filters.agent,
        channel: filters.channel,
        contact: filters.contact,
        status: filters.status,
        unread_only: filters.unreadOnly,
        attention_only: filters.attentionOnly,
        conversations_only: filters.conversationsOnly,
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
      }))
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchInterval: (query) =>
      (query.state.data?.pages.length ?? 0) > POLLED_PAGES ? false : LIST_POLL_MS,
    refetchOnWindowFocus: true,
    // Without this every window focus refetches EVERY loaded page.
    staleTime: 30_000,
  })
}

/**
 * One conversation for the reading pane.
 *
 * Keyed by the prefixed id, so switching rows swaps threads instead of
 * refetching one shared slot, and going back to a thread already read is
 * instant. `null` means the id resolved to nothing this tenant owns — or, with
 * `agent`, nothing that agent owns (honoured by the email source).
 */
export function useUnifiedThread(id: string | null, options: { agent?: string } = {}) {
  const { agent } = options
  return useQuery({
    enabled: !!id,
    queryKey: [...UNIFIED_INBOX_QUERY_KEY, "thread", id, agent ?? null],
    queryFn: async () => {
      const result = await boundedRead(getUnifiedThreadAction({ id: id!, agent }))
      if ("status" in result) {
        if (result.status === 404) return null
        throw new Error(result.errorMessage)
      }
      return result
    },
    refetchInterval: THREAD_POLL_MS,
    staleTime: 30_000,
  })
}

/**
 * Filter values. Fetched once and cached rather than polled: assembling them
 * costs a `DISTINCT` over the tenant's calls, and an agent list does not change
 * while somebody is triaging.
 */
export function useInboxFilterOptions() {
  return useQuery({
    queryKey: [...UNIFIED_INBOX_QUERY_KEY, "filter-options"],
    queryFn: async () => {
      const result = await getInboxFilterOptionsAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useInboxSourceCapabilities() {
  return useQuery({
    queryKey: [...UNIFIED_INBOX_QUERY_KEY, "capabilities"],
    queryFn: async () => {
      const result = await getInboxSourceCapabilitiesAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * The sidebar badge. Its own query key on purpose: it polls, and sharing the
 * list's key would refetch every loaded page of the infinite query with it.
 *
 * React Query does not fire `refetchInterval` in a background tab, so the cost
 * is one invocation a minute per tab someone is actually looking at.
 */
export function useInboxAttentionSummary({
  enabled = true,
  since,
}: { enabled?: boolean; since?: string } = {}) {
  return useQuery({
    enabled,
    // The boundary is part of the key, not just the request: stamping a new visit
    // has to produce a different query, or the refetch returns the same count.
    queryKey: [...UNIFIED_INBOX_QUERY_KEY, "attention-summary", since ?? null],
    queryFn: async () => {
      const result = await getInboxAttentionSummaryAction({ since })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    refetchInterval: 60_000,
    staleTime: 60_000,
  })
}
